import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import {
  ArrowLeft, Save, PanelRight, PanelRightClose, ArrowUpRight,
  Copy, Check, Link2, RefreshCw, Send, ExternalLink, Zap, Sparkles,
  Languages, X, Braces, ArrowUp, ArrowDown, Plus, Pencil,
} from "lucide-react";
import * as api from "../lib/api";
import { gradeColor, AEO_FACTORS, citationGaps } from "../lib/aeo";
import { INTELLIGENCE_DIMENSION_LABELS, INTELLIGENCE_DIMENSION_ORDER } from "../lib/articleIntelligence";
import PlateArticleEditor from "@/components/editor/plate-article-editor";
import { htmlToValue, valueToHtml, valueToPlainText, countWords } from "@/lib/plate-html";
import { useArticleOutline, SOURCE_BADGE } from "../hooks/useArticleOutline";

const PRIMARY = "#018D4C";
const PRIMARY_LIGHT = "#E6F5EE";

const TRANSLATE_LANGUAGES = [
  { code: "EN-US", name: "English (US)" },
  { code: "EN-GB", name: "English (UK)" },
  { code: "ID", name: "Indonesian" },
  { code: "DE", name: "German" },
  { code: "FR", name: "French" },
  { code: "ES", name: "Spanish" },
  { code: "PT-BR", name: "Portuguese (Brazil)" },
  { code: "JA", name: "Japanese" },
  { code: "ZH", name: "Chinese (Simplified)" },
  { code: "KO", name: "Korean" },
  { code: "IT", name: "Italian" },
  { code: "NL", name: "Dutch" },
];
const VERDICT_COLOR = { PASS: "#008A33", PARTIAL: "#FF8C00", NEUTRAL: "#6B7280", FAIL: "#FF2A00" };

function computeOpportunities(article) {
  if (!article) return [];
  const opps = [];
  const pos = article.gsc_position;
  const imps = article.gsc_impressions_28d || 0;
  const ctr = article.gsc_ctr || 0;
  const score = article.content_score;
  const aeo = article.aeo_score;
  if (pos >= 8 && pos <= 20 && imps > 50) {
    opps.push({ label: "Quick Win", desc: `Pos ${pos.toFixed(1)} · ${imps} impressions — push to page 1`, color: "#018D4C" });
  }
  if (imps > 100 && ctr < 0.02) {
    opps.push({ label: "CTR Gap", desc: `${imps} impressions, ${(ctr * 100).toFixed(1)}% CTR — fix title/meta`, color: "#FF8C00" });
  }
  if (score !== null && score !== undefined && score < 50) {
    opps.push({ label: "Content Gap", desc: `Content score ${Math.round(score)}/100 — add depth`, color: "#E67E22" });
  }
  if (aeo !== null && aeo !== undefined && aeo < 50) {
    opps.push({ label: "AI Readiness", desc: `AEO ${Math.round(aeo)}/100 — optimize for AI citation`, color: "#9B59B6" });
  }
  if (article.gsc_matched === false) {
    opps.push({ label: "Not Indexed", desc: "Not in Google index — request indexing", color: "#FF2A00" });
  }
  return opps;
}

// ── Panel helpers ─────────────────────────────────────────────────────────────

function SidebarPanel({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-zinc-100 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-zinc-50">
        <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">{title}</p>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };
  return (
    <button onClick={handle} title="Copy" className="flex-shrink-0 p-1 rounded hover:bg-zinc-100 transition-colors">
      {copied ? <Check size={12} className="text-green-600" strokeWidth={2.5} /> : <Copy size={12} className="text-zinc-400" strokeWidth={2} />}
    </button>
  );
}

function OpportunityPanel({ article }) {
  const opps = computeOpportunities(article);
  if (!opps.length) return (
    <SidebarPanel title="Opportunities">
      <p className="text-xs text-zinc-400">No signals detected — article is well optimized.</p>
    </SidebarPanel>
  );
  return (
    <SidebarPanel title="Opportunities">
      <div className="space-y-2">
        {opps.map((o, i) => (
          <div key={i} className="flex items-start gap-2.5 p-2.5 rounded-lg border" style={{ borderColor: o.color + "30", background: o.color + "08" }}>
            <Zap size={12} className="flex-shrink-0 mt-0.5" style={{ color: o.color }} strokeWidth={2.5} />
            <div className="min-w-0">
              <p className="text-[11px] font-bold" style={{ color: o.color }}>{o.label}</p>
              <p className="text-[11px] text-zinc-500 mt-0.5">{o.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </SidebarPanel>
  );
}

function RankingAttributionPanel({ article }) {
  const queries = article?.gsc_top_queries;
  return (
    <SidebarPanel title="Ranking Signals">
      {!queries?.length ? (
        <p className="text-xs text-zinc-400">No GSC query data — sync GSC first.</p>
      ) : (
        <div className="space-y-2">
          <p className="text-[11px] text-zinc-400 mb-3">Queries driving this article's ranking:</p>
          {queries.map((q, i) => (
            <div key={i} className="p-2.5 bg-zinc-50 border border-zinc-100 rounded-lg">
              <p className="text-[11px] font-medium text-zinc-900 line-clamp-1">{q.query}</p>
              <div className="flex gap-3 mt-1">
                <span className="text-[10px] text-zinc-400">{q.clicks} clicks</span>
                <span className="text-[10px] text-zinc-400">pos {q.position?.toFixed?.(1) ?? "—"}</span>
                {q.impressions && <span className="text-[10px] text-zinc-400">{q.impressions} impr</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </SidebarPanel>
  );
}

const ENTITY_TYPE_COLORS = {
  location: "#3498DB",
  entity: "#9B59B6",
  concept: "#018D4C",
  service: "#FF8C00",
  claim: "#E74C3C",
  topic: "#1ABC9C",
};

function EntityPanel({ article, onExtract, extracting }) {
  const data = article?.entities;
  const entities = data?.entities || [];
  const hasEntities = entities.length > 0;
  const errorMsg = data?.error;

  return (
    <SidebarPanel title="Entity Intelligence">
      {!hasEntities ? (
        <div className="text-center py-2">
          <Sparkles size={20} className="mx-auto text-zinc-300 mb-2" />
          <p className="text-[11px] text-zinc-400 mb-1 leading-relaxed">
            {errorMsg
              ? <span className="text-red-400">{errorMsg}</span>
              : "Extract semantic entities via Google LangExtract + gemma4:31b-cloud"}
          </p>
          <button
            onClick={onExtract}
            disabled={extracting}
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold text-white rounded-lg disabled:opacity-50 transition-colors"
            style={{ backgroundColor: PRIMARY }}
          >
            <Sparkles size={11} strokeWidth={2} />
            {extracting ? "Extracting…" : "Run Extraction"}
          </button>
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-[10px] text-zinc-400">{entities.length} entities · {data.model}</p>
            <button
              onClick={onExtract}
              disabled={extracting}
              className="text-[10px] font-bold text-zinc-400 hover:text-zinc-700 disabled:opacity-40 transition-colors"
            >
              {extracting ? "…" : "Re-run"}
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {entities.map((e, i) => {
              const color = ENTITY_TYPE_COLORS[e.type] || "#6B7280";
              return (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
                  style={{ background: color + "15", color }}
                  title={e.type}
                >
                  {e.text}
                  {e.count > 1 && <span className="opacity-50 text-[9px]">×{e.count}</span>}
                </span>
              );
            })}
          </div>
          {data.extracted_at && (
            <p className="text-[9px] text-zinc-300 mt-3">
              {new Date(data.extracted_at).toLocaleString()}
            </p>
          )}
        </div>
      )}
    </SidebarPanel>
  );
}

function SchemaMarkupPanel({ article }) {
  const [jsonLd, setJsonLd] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const hasEntities = (article?.entities?.entities || []).length > 0;
  if (!hasEntities) return null;

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      setJsonLd(await api.getArticleSchemaJsonLd(article.wp_post_id));
    } catch (e) {
      setError(e.response?.data?.detail || "Schema generation failed.");
    } finally {
      setGenerating(false);
    }
  };

  const snippet = jsonLd
    ? `<script type="application/ld+json">\n${JSON.stringify(jsonLd, null, 2)}\n</script>`
    : "";

  return (
    <SidebarPanel title="Schema Markup">
      {!jsonLd ? (
        <div className="text-center py-2">
          <Braces size={20} className="mx-auto text-zinc-300 mb-2" />
          <p className="text-[11px] text-zinc-400 mb-2 leading-relaxed">
            {error
              ? <span className="text-red-400">{error}</span>
              : "Build BlogPosting JSON-LD from extracted entities for AI citation."}
          </p>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="mt-1 inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold text-white rounded-lg disabled:opacity-50 transition-colors"
            style={{ backgroundColor: PRIMARY }}
          >
            <Braces size={11} strokeWidth={2} />
            {generating ? "Generating…" : "Generate Schema"}
          </button>
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] text-zinc-400">{(jsonLd.mentions || []).length} mentions</p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="text-[10px] font-bold text-zinc-400 hover:text-zinc-700 disabled:opacity-40 transition-colors"
              >
                {generating ? "…" : "Re-run"}
              </button>
              <CopyButton text={snippet} />
            </div>
          </div>
          <pre className="text-[10px] text-zinc-600 bg-zinc-50 border border-zinc-100 rounded-lg p-2.5 overflow-x-auto max-h-64 whitespace-pre-wrap break-words">
            {snippet}
          </pre>
          <p className="text-[9px] text-zinc-300 mt-2">Copy into the article's `&lt;head&gt;` — not auto-injected.</p>
        </div>
      )}
    </SidebarPanel>
  );
}

function ScoreSummaryPanel({ article }) {
  if (article?.content_score == null && article?.aeo_score == null) return null;
  return (
    <SidebarPanel title="Scores">
      <div className="grid grid-cols-2 gap-3">
        {article.content_score != null && (
          <div className="text-center p-3 bg-zinc-50 rounded-lg">
            <p className="text-2xl font-bold tabular-nums" style={{ color: gradeColor(article.content_grade) }}>
              {Math.round(article.content_score)}
            </p>
            <p className="text-[9px] tracking-widest uppercase text-zinc-400 mt-0.5">Content</p>
          </div>
        )}
        {article.aeo_score != null && (
          <div className="text-center p-3 bg-zinc-50 rounded-lg">
            <p className="text-2xl font-bold tabular-nums" style={{ color: gradeColor(article.aeo_grade) }}>
              {Math.round(article.aeo_score)}
            </p>
            <p className="text-[9px] tracking-widest uppercase text-zinc-400 mt-0.5">AEO</p>
          </div>
        )}
      </div>
      {article.content_breakdown && (
        <div className="mt-3 space-y-1.5">
          {[
            { key: "word_count", label: "Word count", max: 30 },
            { key: "has_cluster", label: "Cluster fit", max: 15 },
            { key: "linking_opportunities", label: "Link opportunities", max: 20 },
            { key: "recency", label: "Recency", max: 15 },
            { key: "topic_depth", label: "Topic depth", max: 20 },
          ].map(it => {
            const v = article.content_breakdown[it.key] ?? 0;
            const c = gradeColor(article.content_grade);
            return (
              <div key={it.key} className="flex items-center gap-2 text-[10px]">
                <span className="w-28 text-zinc-400 truncate">{it.label}</span>
                <div className="flex-1 h-1 bg-zinc-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(v / it.max) * 100}%`, backgroundColor: c }} />
                </div>
                <span className="w-8 text-right tabular-nums text-zinc-600">{v}/{it.max}</span>
              </div>
            );
          })}
        </div>
      )}
    </SidebarPanel>
  );
}

function AEODetailPanel({ article }) {
  if (article?.aeo_score == null || !article?.aeo_breakdown) return null;
  const c = gradeColor(article.aeo_grade);
  const gaps = citationGaps(article.aeo_breakdown);
  return (
    <SidebarPanel title="AI Citation Readiness">
      <div className="space-y-1.5">
        {AEO_FACTORS.map(it => {
          const v = article.aeo_breakdown[it.key] ?? 0;
          return (
            <div key={it.key} className="flex items-center gap-2 text-[10px]">
              <span className="w-28 text-zinc-400 truncate">{it.label}</span>
              <div className="flex-1 h-1 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${(v / it.max) * 100}%`, backgroundColor: c }} />
              </div>
              <span className="w-8 text-right tabular-nums text-zinc-600">{v}/{it.max}</span>
            </div>
          );
        })}
      </div>
      {gaps.length > 0 && (
        <div className="mt-3 pt-3 border-t border-zinc-100 space-y-2">
          <p className="text-[9px] font-bold uppercase tracking-wider text-zinc-400">Copywriting to-do</p>
          {gaps.map(g => (
            <div key={g.key} className="flex items-start gap-1.5 text-[10px] text-zinc-600 leading-snug">
              <span className="shrink-0 mt-0.5" style={{ color: gradeColor(article.aeo_grade) }}>&bull;</span>
              <span><b className="text-zinc-800">{g.label}:</b> {g.tip}</span>
            </div>
          ))}
        </div>
      )}
    </SidebarPanel>
  );
}

const VERDICT_TONE = { strong: "#008A33", moderate: "#FF8C00", weak: "#E67E22", poor: "#FF2A00" };

/** Outline-first rewrite: extract the article's section outline, let the writer
 * reorder/remove/add sections (incl. GSC/fan-out-grounded gap suggestions), then
 * rewrite the article against the edited outline via onRewriteFromOutline. */
function OutlinePanel({ articleId, rewriting, onRewriteFromOutline }) {
  const {
    loading, error, ready, dirty, sections,
    move, remove, gapsState, findGaps, addGap, dismissGap, jumpAt, toPayload,
  } = useArticleOutline(articleId);
  const { gaps, gapsLoading, gapError } = gapsState;

  return (
    <SidebarPanel title="Outline — restructure before rewriting">
      {loading ? (
        <p className="text-[11px] text-zinc-400">Extracting outline…</p>
      ) : error ? (
        <p className="text-[11px] text-red-400">{error}</p>
      ) : ready && (
        <div className="space-y-1.5">
          <Link to={`/dashboard/articles/${articleId}/outline`} className="text-[10px] font-bold text-zinc-400 hover:text-zinc-700 transition-colors">
            Open full view →
          </Link>
          <div className="space-y-0.5">
            {sections.map((s, i) => {
              // jump marker between this section and the next, if gap analysis flagged a break in the thread
              const jump = jumpAt(i);
              return (
                <div key={s.key}>
                  <div className="flex items-center gap-1 group" style={{ paddingLeft: `${(Math.min(s.level, 4) - 2) * 8}px` }}>
                    <div className="flex-1 min-w-0 flex items-center gap-1.5">
                      <span className="text-[11px] font-bold text-zinc-800 truncate">{s.heading}</span>
                      {s.added ? (
                        <span className="px-1 py-px text-[8px] font-bold uppercase rounded bg-indigo-50 text-indigo-700 flex-shrink-0">New</span>
                      ) : s.words > 0 && (
                        <span className="text-[9px] text-zinc-300 tabular-nums flex-shrink-0">{s.words}w</span>
                      )}
                    </div>
                    <button onClick={() => move(i, -1)} disabled={i === 0} className="flex-shrink-0 p-0.5 text-zinc-300 hover:text-zinc-700 disabled:opacity-30 transition-colors" title="Move up">
                      <ArrowUp size={11} strokeWidth={2.5} />
                    </button>
                    <button onClick={() => move(i, 1)} disabled={i === sections.length - 1} className="flex-shrink-0 p-0.5 text-zinc-300 hover:text-zinc-700 disabled:opacity-30 transition-colors" title="Move down">
                      <ArrowDown size={11} strokeWidth={2.5} />
                    </button>
                    <button onClick={() => remove(i)} className="flex-shrink-0 p-0.5 text-zinc-300 hover:text-red-500 transition-colors" title="Remove section">
                      <X size={11} strokeWidth={2.5} />
                    </button>
                  </div>
                  {jump && (
                    <div className="ml-2 my-0.5 pl-2 border-l-2 border-amber-300 text-[9px] text-amber-600 leading-snug" title={`Thought jump between "${jump.between[0]}" and "${jump.between[1]}"`}>
                      ⚠ jump: {jump.why || "no logical bridge"}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {!gaps && !gapsLoading && (
            <button onClick={findGaps} className="text-[10px] font-bold text-zinc-400 hover:text-zinc-700 transition-colors">
              Find coverage gaps →
            </button>
          )}
          {gapsLoading && <p className="text-[10px] text-zinc-400">Comparing against real audience questions…</p>}
          {gapError && <p className="text-[10px] text-red-400">{gapError}</p>}

          {gaps && (
            <div className="space-y-1.5 pt-1">
              {gaps.ordering_note && <p className="text-[10px] text-amber-600 leading-snug">⚠ {gaps.ordering_note}</p>}
              {gaps.missing.length === 0 && !gaps.ordering_note && (
                <p className="text-[10px] text-green-600 font-bold">No uncovered questions — outline covers the real demand.</p>
              )}
              {gaps.missing.map((gap, i) => (
                <div key={i} className="rounded-lg border border-zinc-100 bg-zinc-50 p-1.5 space-y-1">
                  <div className="flex items-start justify-between gap-1">
                    <span className="text-[10px] font-bold text-zinc-800 leading-snug">{gap.heading}</span>
                    <span className={`px-1 py-px text-[8px] font-bold uppercase rounded flex-shrink-0 ${SOURCE_BADGE[gap.source]?.cls || SOURCE_BADGE.topic.cls}`}>
                      {SOURCE_BADGE[gap.source]?.label || "Topic"}
                    </span>
                  </div>
                  {gap.answers && <p className="text-[9px] text-zinc-400 leading-snug">answers: “{gap.answers}”</p>}
                  {gap.reason && <p className="text-[9px] text-zinc-400 leading-snug">{gap.reason}</p>}
                  <div className="flex items-center gap-2 pt-0.5">
                    <button onClick={() => addGap(gap)} className="inline-flex items-center gap-0.5 text-[10px] font-bold text-zinc-500 hover:text-zinc-900 transition-colors">
                      <Plus size={10} strokeWidth={2.5} /> Add section
                    </button>
                    <button onClick={() => dismissGap(gap)} className="text-[9px] text-zinc-300 hover:text-zinc-500 transition-colors">dismiss</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="pt-2 mt-1 border-t border-zinc-100">
            {dirty ? (
              <button
                onClick={() => onRewriteFromOutline(toPayload())}
                disabled={rewriting}
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-[11px] font-bold text-white rounded-lg disabled:opacity-50 transition-colors"
                style={{ backgroundColor: "#111827" }}
              >
                <Sparkles size={11} strokeWidth={2} />
                {rewriting ? "Rewriting…" : "Improve from outline"}
              </button>
            ) : (
              <p className="text-[10px] text-zinc-400">Reorder, remove, or add sections — then rewrite against this structure.</p>
            )}
          </div>
        </div>
      )}
    </SidebarPanel>
  );
}

function ArticleIntelligencePanel({
  article, onScore, scoring, scoreError,
  onRewrite, rewriting, rewriteError, rewriteResult,
  onApply, applying, applyError, applied,
}) {
  const data = article?.article_intelligence;
  const dims = data?.dimensions;
  const hasWeak = dims && Object.values(dims).some(d => d.type === "score" && (d.grade === "weak" || d.grade === "poor"));

  return (
    <SidebarPanel title="Article Intelligence">
      {!dims ? (
        <div className="text-center py-2">
          <Sparkles size={20} className="mx-auto text-zinc-300 mb-2" />
          <p className="text-[11px] text-zinc-400 mb-1 leading-relaxed">
            {scoreError
              ? <span className="text-red-400">{scoreError}</span>
              : data?.error
                ? <span className="text-red-400">{data.error}</span>
                : `Judge this article across ${INTELLIGENCE_DIMENSION_ORDER.length} atomic dimensions (persona, intent, evidence, CTA fit…) via TypeSafe`}
          </p>
          <button
            onClick={onScore}
            disabled={scoring}
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold text-white rounded-lg disabled:opacity-50 transition-colors"
            style={{ backgroundColor: PRIMARY }}
          >
            <Sparkles size={11} strokeWidth={2} />
            {scoring ? "Scoring…" : "Run Article Intelligence"}
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-zinc-400">{data.model}</p>
            <button
              onClick={onScore}
              disabled={scoring}
              className="text-[10px] font-bold text-zinc-400 hover:text-zinc-700 disabled:opacity-40 transition-colors"
            >
              {scoring ? "…" : "Re-run"}
            </button>
          </div>
          {INTELLIGENCE_DIMENSION_ORDER.map((key) => {
            const d = dims[key];
            if (!d) return null;
            const label = INTELLIGENCE_DIMENSION_LABELS[key];
            if (d.type === "choice") {
              return (
                <div key={key} className="flex items-center justify-between text-[11px]">
                  <span className="text-zinc-600">{label}</span>
                  <span className="font-bold text-zinc-900 capitalize">{(d.choice || "").replace(/_/g, " ")}</span>
                </div>
              );
            }
            const c = gradeColor(d.grade);
            return (
              <div key={key}>
                <div className="flex items-center justify-between text-[11px] mb-0.5">
                  <span className="text-zinc-600">{label}</span>
                  <span className="font-bold tabular-nums" style={{ color: c }}>{d.score}</span>
                </div>
                <div className="w-full h-1 bg-zinc-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${d.score}%`, backgroundColor: c }} />
                </div>
              </div>
            );
          })}

          {hasWeak && (
            <div className="pt-3 mt-1 border-t border-zinc-100">
              {!rewriteResult ? (
                <div className="text-center">
                  <p className="text-[10px] text-zinc-400 mb-1.5">
                    {rewriteError || "Rewrite the article via OpenAI, targeting only the dimensions above that scored weak/poor"}
                  </p>
                  <button
                    onClick={onRewrite}
                    disabled={rewriting}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold text-white rounded-lg disabled:opacity-50 transition-colors"
                    style={{ backgroundColor: "#111827" }}
                  >
                    <Sparkles size={11} strokeWidth={2} />
                    {rewriting ? "Rewriting…" : "Improve Article"}
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-[10px] text-zinc-400">{rewriteResult.model} · {rewriteResult.outline_driven ? "outline-driven · " : ""}targeted {rewriteResult.targeted_dimensions?.length} dimension(s)</p>
                  <p className="text-[11px] text-zinc-700 leading-relaxed">{rewriteResult.summary}</p>
                  {rewriteResult.sections_changed?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {rewriteResult.sections_changed.map((s, i) => (
                        <span key={i} className="px-1.5 py-0.5 text-[10px] bg-zinc-100 text-zinc-600 rounded-full">{s}</span>
                      ))}
                    </div>
                  )}
                  {rewriteResult.structural_changes?.length > 0 && (
                    <div className="space-y-1 pt-1">
                      <p className="text-[9px] font-bold uppercase tracking-wider text-zinc-400">Structural changes (Minto Pyramid)</p>
                      {rewriteResult.structural_changes.map((c, i) => (
                        <div key={i} className="flex items-start gap-1.5 text-[11px] leading-snug">
                          <span className="px-1 py-px mt-px text-[9px] font-bold uppercase rounded flex-shrink-0 bg-indigo-50 text-indigo-700">{c.action}</span>
                          <span className="text-zinc-600">
                            <span className="font-bold text-zinc-800">{c.section}</span>
                            {c.detail ? ` — ${c.detail}` : ""}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {rewriteResult.after && (
                    <div className="space-y-1 pt-1">
                      <p className="text-[9px] font-bold uppercase tracking-wider text-zinc-400">Before → After</p>
                      {rewriteResult.targeted_dimensions.map((key) => {
                        const b = rewriteResult.before?.[key];
                        const a = rewriteResult.after?.[key];
                        if (!b || !a || a.type !== "score") return null;
                        const delta = a.score - b.score;
                        return (
                          <div key={key} className="flex items-center justify-between text-[11px]">
                            <span className="text-zinc-600">{INTELLIGENCE_DIMENSION_LABELS[key]}</span>
                            <span className="tabular-nums">
                              <span style={{ color: gradeColor(b.grade) }}>{b.score}</span>
                              <span className="text-zinc-300 mx-1">→</span>
                              <span className="font-bold" style={{ color: gradeColor(a.grade) }}>{a.score}</span>
                              <span className={delta >= 0 ? "text-green-600 ml-1" : "text-red-500 ml-1"}>
                                {delta >= 0 ? "↑" : "↓"}
                              </span>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-[10px] font-bold text-zinc-400">Revised article</span>
                    <CopyButton text={rewriteResult.revised_content} />
                  </div>
                  <p className="text-[11px] text-zinc-600 leading-relaxed max-h-32 overflow-y-auto bg-zinc-50 rounded-lg p-2">
                    {rewriteResult.revised_content}
                  </p>
                  {applyError && <p className="text-[10px] text-red-400">{applyError}</p>}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={onApply}
                      disabled={applying || applied}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold text-white rounded-lg disabled:opacity-50 transition-colors"
                      style={{ backgroundColor: applied ? "#008A33" : PRIMARY }}
                    >
                      {applied ? "Applied to site" : applying ? "Applying…" : "Apply to Site"}
                    </button>
                    <button
                      onClick={onRewrite}
                      disabled={rewriting}
                      className="text-[10px] font-bold text-zinc-400 hover:text-zinc-700 disabled:opacity-40 transition-colors"
                    >
                      {rewriting ? "…" : "Re-run"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {article?.rewrite_applied && (
            <div className="pt-3 mt-1 border-t border-zinc-100">
              <p className="text-[9px] font-bold uppercase tracking-wider text-zinc-400 mb-1.5">
                Real-World Impact — applied {new Date(article.rewrite_applied.applied_at).toLocaleDateString()}
              </p>
              {[
                { key: "gsc_position", label: "Position", lowerIsBetter: true, fmt: v => v?.toFixed?.(1) ?? "—" },
                { key: "gsc_clicks_28d", label: "Clicks (28d)", lowerIsBetter: false, fmt: v => v ?? "—" },
                { key: "gsc_impressions_28d", label: "Impressions (28d)", lowerIsBetter: false, fmt: v => v ?? "—" },
              ].map(({ key, label, lowerIsBetter, fmt }) => {
                const before = article.rewrite_applied.gsc_snapshot?.[key];
                const now = article[key];
                if (before == null || now == null) return null;
                const better = lowerIsBetter ? now < before : now > before;
                const changed = now !== before;
                return (
                  <div key={key} className="flex items-center justify-between text-[11px]">
                    <span className="text-zinc-600">{label}</span>
                    <span className="tabular-nums">
                      <span className="text-zinc-400">{fmt(before)}</span>
                      <span className="text-zinc-300 mx-1">→</span>
                      <span className="font-bold text-zinc-900">{fmt(now)}</span>
                      {changed && (
                        <span className={better ? "text-green-600 ml-1" : "text-red-500 ml-1"}>
                          {better ? "↑" : "↓"}
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
              <p className="text-[10px] text-zinc-400 mt-1">GSC metrics update on the next refresh — this compares against the most recent sync.</p>
            </div>
          )}
        </div>
      )}
    </SidebarPanel>
  );
}

function PersonaFitPanel({ article, onScore, scoring, scoreError }) {
  const data = article?.persona_fit;
  const results = data?.results || [];

  return (
    <SidebarPanel title="Buyer Persona Fit">
      {!results.length ? (
        <div className="text-center py-2">
          <Sparkles size={20} className="mx-auto text-zinc-300 mb-2" />
          <p className="text-[11px] text-zinc-400 mb-1 leading-relaxed">
            {scoreError
              ? <span className="text-red-400">{scoreError}</span>
              : data?.error
                ? <span className="text-red-400">{data.error}</span>
                : "Score how well this article speaks to each defined buyer persona"}
          </p>
          <button
            onClick={onScore}
            disabled={scoring}
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold text-white rounded-lg disabled:opacity-50 transition-colors"
            style={{ backgroundColor: PRIMARY }}
          >
            <Sparkles size={11} strokeWidth={2} />
            {scoring ? "Scoring…" : "Score Persona Fit"}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-zinc-400">{data.model}</p>
            <button
              onClick={onScore}
              disabled={scoring}
              className="text-[10px] font-bold text-zinc-400 hover:text-zinc-700 disabled:opacity-40 transition-colors"
            >
              {scoring ? "…" : "Re-run"}
            </button>
          </div>
          {results
            .slice()
            .sort((a, b) => b.score - a.score)
            .map((r, i) => {
              const c = VERDICT_TONE[r.verdict] || "#6B7280";
              const isBest = r.persona === data.best_match;
              return (
                <div key={i} className="p-2.5 bg-zinc-50 border border-zinc-100 rounded-lg">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-bold text-zinc-900 line-clamp-1">
                      {r.persona}{isBest && <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wider" style={{ color: PRIMARY }}>Best fit</span>}
                    </span>
                    <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-bold tabular-nums rounded-full" style={{ background: c + "18", color: c }}>
                      {r.score}
                    </span>
                  </div>
                  <div className="w-full h-1 bg-zinc-100 rounded-full overflow-hidden mt-1.5">
                    <div className="h-full rounded-full" style={{ width: `${r.score}%`, backgroundColor: c }} />
                  </div>
                  {r.gaps?.length > 0 && (
                    <div className="mt-1.5 space-y-1">
                      {r.gaps.map((g, gi) => (
                        <div key={gi} className="flex items-start gap-1.5 text-[10px] text-zinc-600 leading-snug">
                          <span className="shrink-0 mt-0.5" style={{ color: c }}>&bull;</span>
                          <span>{g}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}
    </SidebarPanel>
  );
}

function GSCPanel({ article }) {
  if (article?.gsc_clicks_28d === undefined && !article?.gsc_top_queries?.length) return null;
  const clicks = article.gsc_clicks_28d ?? 0;
  const imps = article.gsc_impressions_28d ?? 0;
  const ctr = article.gsc_ctr ?? 0;
  const pos = article.gsc_position ?? 0;
  return (
    <SidebarPanel title="Search Performance · 28 days">
      <div className="grid grid-cols-2 gap-2 text-center mb-3">
        <div className="bg-zinc-50 rounded-lg py-2">
          <p className="text-xl font-bold tabular-nums text-zinc-900">{clicks}</p>
          <p className="text-[9px] tracking-widest uppercase text-zinc-400 mt-0.5">Clicks</p>
        </div>
        <div className="bg-zinc-50 rounded-lg py-2">
          <p className="text-xl font-bold tabular-nums text-zinc-900">{imps}</p>
          <p className="text-[9px] tracking-widest uppercase text-zinc-400 mt-0.5">Impressions</p>
        </div>
        <div className="bg-zinc-50 rounded-lg py-2">
          <p className="text-xl font-bold tabular-nums text-zinc-900">{(ctr * 100).toFixed(1)}<span className="text-xs">%</span></p>
          <p className="text-[9px] tracking-widest uppercase text-zinc-400 mt-0.5">CTR</p>
        </div>
        <div className="bg-zinc-50 rounded-lg py-2">
          <p className="text-xl font-bold tabular-nums text-zinc-900">{pos > 0 ? pos.toFixed(1) : "—"}</p>
          <p className="text-[9px] tracking-widest uppercase text-zinc-400 mt-0.5">Position</p>
        </div>
      </div>
      {article.gsc_top_queries?.length > 0 && (
        <div className="space-y-1">
          {article.gsc_top_queries.map((q, i) => (
            <div key={i} className="flex items-center justify-between text-[11px] gap-2">
              <span className="text-zinc-900 truncate flex-1">{q.query}</span>
              <span className="text-zinc-400 tabular-nums flex-shrink-0">{q.clicks}cl · p{q.position?.toFixed?.(1) ?? "—"}</span>
            </div>
          ))}
        </div>
      )}
    </SidebarPanel>
  );
}

function LinksPanel({ article }) {
  const suggestions = article?.link_suggestions;
  if (!suggestions?.length) return null;
  return (
    <SidebarPanel title="Suggested Internal Links">
      <p className="text-[10px] text-zinc-400 mb-3">Add to improve fan-out rank.</p>
      <div className="space-y-1.5">
        {suggestions.map((s) => (
          <div key={s.wp_post_id} className="flex items-center gap-2 p-2 bg-zinc-50 border border-zinc-100 rounded-lg">
            <span className="flex-1 text-[11px] text-zinc-900 line-clamp-1 min-w-0">{s.title}</span>
            <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-bold tabular-nums rounded-full" style={{ background: PRIMARY_LIGHT, color: PRIMARY }}>
              {Math.round(s.similarity_score * 100)}%
            </span>
            <CopyButton text={s.wp_url} />
            {s.wp_url && (
              <a href={s.wp_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="flex-shrink-0 opacity-50 hover:opacity-100 transition-opacity">
                <ArrowUpRight size={11} className="text-zinc-900" />
              </a>
            )}
          </div>
        ))}
      </div>
    </SidebarPanel>
  );
}

function IndexingPanel({ article, gscPropertyUrl }) {
  const [inspectResult, setInspectResult] = useState(null);
  const [inspecting, setInspecting] = useState(false);
  const [inspectError, setInspectError] = useState(null);
  const [submitState, setSubmitState] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  if (!article?.wp_url || !gscPropertyUrl) return null;

  const liveTestUrl = `https://search.google.com/search-console/inspect?resource_id=${encodeURIComponent(gscPropertyUrl)}&url=${encodeURIComponent(article.wp_url)}`;

  const handleInspect = async () => {
    setInspecting(true);
    setInspectError(null);
    try { setInspectResult(await api.inspectUrl(article.wp_url)); }
    catch (e) { setInspectError(e.response?.data?.detail || "Inspection failed."); }
    finally { setInspecting(false); }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitState(null);
    try {
      const r = await api.submitIndexingOne(article.wp_url, article.wp_post_id);
      setSubmitState({ success: true, message: r.message, remaining: r.remaining });
    } catch (e) {
      setSubmitState({ success: false, message: e.response?.data?.detail || "Submit failed." });
    } finally { setSubmitting(false); }
  };

  const vc = inspectResult ? (VERDICT_COLOR[inspectResult.verdict] ?? "#6B7280") : null;

  return (
    <SidebarPanel title="Google Indexing">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-zinc-500">GSC match:</span>
            {article.gsc_matched === true && <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-green-100 text-green-700">Indexed</span>}
            {article.gsc_matched === false && <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-orange-100 text-orange-700">Not indexed</span>}
            {(article.gsc_matched == null) && <span className="text-[11px] text-zinc-400">Unknown</span>}
          </div>
          <a href={liveTestUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] font-bold text-zinc-400 hover:text-zinc-900 transition-colors">
            Live Test <ExternalLink size={9} strokeWidth={2} />
          </a>
        </div>

        {inspectResult && (
          <div className="bg-zinc-50 border border-zinc-100 rounded-lg p-3 space-y-1 text-[11px]">
            <div className="flex items-center justify-between">
              <span className="font-bold text-zinc-400 uppercase tracking-widest text-[9px]">Cached status</span>
              {inspectResult.verdict && (
                <span className="font-bold px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest" style={{ background: vc + "18", color: vc }}>{inspectResult.verdict}</span>
              )}
            </div>
            {inspectResult.coverage_state && <div><span className="text-zinc-400">Coverage: </span><span className="font-medium text-zinc-900">{inspectResult.coverage_state}</span></div>}
            {inspectResult.last_crawl_time && <div><span className="text-zinc-400">Crawled: </span><span className="text-zinc-900">{new Date(inspectResult.last_crawl_time).toLocaleString()}</span></div>}
            {inspectResult.inspection_result_link && (
              <a href={inspectResult.inspection_result_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] font-bold mt-1 hover:underline" style={{ color: PRIMARY }}>
                View in Search Console <ExternalLink size={9} strokeWidth={2} />
              </a>
            )}
          </div>
        )}

        {inspectError && <p className="text-[11px] text-red-500">{inspectError}</p>}

        {submitState && (
          <div className={`text-[11px] px-3 py-2 rounded-lg border ${submitState.success ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-600"}`}>
            {submitState.message}
            {submitState.success && submitState.remaining !== undefined && <span className="text-green-500 ml-1">({submitState.remaining} quota left)</span>}
          </div>
        )}

        <div className="flex gap-2 flex-wrap">
          <button type="button" onClick={handleInspect} disabled={inspecting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold border border-zinc-200 text-zinc-600 hover:bg-zinc-50 disabled:opacity-40 transition-colors rounded-lg">
            <RefreshCw size={11} strokeWidth={2} className={inspecting ? "animate-spin" : ""} />
            {inspecting ? "Checking..." : "Check Status"}
          </button>
          <button type="button" onClick={handleSubmit} disabled={submitting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold text-white disabled:opacity-40 transition-colors rounded-lg"
            style={{ backgroundColor: PRIMARY }}>
            <Send size={11} strokeWidth={2} />
            {submitting ? "Requesting..." : "Request Indexing"}
          </button>
        </div>
      </div>
    </SidebarPanel>
  );
}

function TopicsPanel({ article }) {
  if (!article?.primary_topic && !article?.topic_keywords?.length) return null;
  return (
    <SidebarPanel title="Topic Tags">
      <div className="flex flex-wrap gap-1.5">
        {article.primary_topic && (
          <span className="px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase rounded-full bg-zinc-900 text-white">{article.primary_topic}</span>
        )}
        {article.topic_keywords?.map(k => (
          <span key={k} className="px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase rounded-full bg-white border border-zinc-100 text-zinc-500">{k}</span>
        ))}
      </div>
    </SidebarPanel>
  );
}

function RelatedPanel({ article }) {
  const related = article?.related_articles;
  if (!related?.length) return null;
  return (
    <SidebarPanel title="Related Articles">
      <div className="space-y-1.5">
        {related.map(ra => (
          <div key={ra.wp_post_id} className="flex items-center justify-between gap-2 p-2 bg-zinc-50 border border-zinc-100 rounded-lg">
            <span className="text-[11px] text-zinc-900 flex-1 line-clamp-1">{ra.title}</span>
            <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-bold tabular-nums rounded-full" style={{ background: PRIMARY_LIGHT, color: PRIMARY }}>
              {Math.round(ra.similarity_score * 100)}%
            </span>
          </div>
        ))}
      </div>
    </SidebarPanel>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ArticleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contentReady, setContentReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  // Open by default; the header toggle persists the preference so a
  // distraction-free session stays distraction-free after navigation.
  const [sidebarOpen, setSidebarOpen] = useState(
    () => localStorage.getItem("article.sidebarOpen") !== "false"
  );
  useEffect(() => {
    localStorage.setItem("article.sidebarOpen", String(sidebarOpen));
  }, [sidebarOpen]);
  const [gscPropertyUrl, setGscPropertyUrl] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [scoringPersona, setScoringPersona] = useState(false);
  const [scoringIntel, setScoringIntel] = useState(false);
  const [intelScoreError, setIntelScoreError] = useState(null);
  const [rewriting, setRewriting] = useState(false);
  const [rewriteError, setRewriteError] = useState(null);
  const [rewriteResult, setRewriteResult] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [applied, setApplied] = useState(false);
  const [personaScoreError, setPersonaScoreError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const [wordCount, setWordCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [showTranslateModal, setShowTranslateModal] = useState(false);
  const [translateLang, setTranslateLang] = useState("");
  const [translating, setTranslating] = useState(false);
  const [translateResult, setTranslateResult] = useState(null);
  const [translateError, setTranslateError] = useState(null);
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [hasCustomComponents, setHasCustomComponents] = useState(false);
  // Inline SEO fields: title doubles as the page/meta title in Astro
  // frontmatter; description is the meta description.
  const [metaDescription, setMetaDescription] = useState("");
  const [editingMeta, setEditingMeta] = useState(null); // null | "title" | "description"
  const [titleDraft, setTitleDraft] = useState("");
  const [descDraft, setDescDraft] = useState("");
  const [savingMeta, setSavingMeta] = useState(false);
  const [metaError, setMetaError] = useState(null);
  const cancelMetaRef = useRef(false);

  // Plate stores its Slate value here; the editor is remounted via editorKey
  // whenever content is reloaded (initial load, refresh, apply-rewrite).
  const editorRef = useRef(null);
  const [editorValue, setEditorValue] = useState(null);
  const [editorKey, setEditorKey] = useState(0);

  const loadEditorContent = ({ content_html, has_custom_components, description }) => {
    const value = htmlToValue(content_html);
    setEditorValue(value);
    setEditorKey(k => k + 1);
    setHasCustomComponents(!!has_custom_components);
    setWordCount(countWords(value));
    setMetaDescription(description || "");
  };

  useEffect(() => {
    const artId = parseInt(id);
    Promise.all([
      api.getArticle(artId),
      api.getGscSettings().catch(() => null),
    ]).then(([art, gsc]) => {
      setArticle(art);
      if (gsc?.verified && gsc?.configured) setGscPropertyUrl(gsc.property_url || "");
    }).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!article) return;
    api.getArticleRawContent(parseInt(id))
      .then((content) => {
        loadEditorContent(content);
        setContentReady(true);
      })
      .catch(() => setContentReady(true));
  }, [article?.wp_post_id]);

  // Handoff from ArticleOutlinePage: "Improve from outline" navigates here with
  // the edited outline in location.state — run the rewrite immediately. Guards:
  // the ref blocks the post-replace effect re-run and StrictMode double-fire;
  // the replace clears history state so F5/Back can't refire a rewrite.
  const location = useLocation();
  const outlineConsumedRef = useRef(false);
  useEffect(() => {
    const outline = location.state?.outline;
    if (!Array.isArray(outline) || outlineConsumedRef.current) return;
    outlineConsumedRef.current = true;
    setSidebarOpen(true);                        // rewrite preview lives in the sidebar
    navigate(".", { replace: true, state: {} });
    handleRewrite(outline);
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExtractEntities = async () => {
    setExtracting(true);
    try {
      const result = await api.extractArticleEntities(parseInt(id));
      setArticle(prev => ({ ...prev, entities: result }));
    } catch (e) {
      console.error("Entity extraction failed:", e);
    } finally {
      setExtracting(false);
    }
  };

  const handleScorePersona = async () => {
    setScoringPersona(true);
    setPersonaScoreError(null);
    try {
      const result = await api.scorePersonaFit(parseInt(id));
      setArticle(prev => ({ ...prev, persona_fit: result }));
    } catch (e) {
      setPersonaScoreError(e.response?.data?.detail || "Persona scoring failed");
    } finally {
      setScoringPersona(false);
    }
  };

  const handleScoreIntelligence = async () => {
    setScoringIntel(true);
    setIntelScoreError(null);
    setRewriteResult(null);
    setApplied(false);
    setApplyError(null);
    try {
      const result = await api.scoreArticleIntelligence(parseInt(id));
      setArticle(prev => ({ ...prev, article_intelligence: result }));
    } catch (e) {
      setIntelScoreError(e.response?.data?.detail || "Article Intelligence scoring failed");
    } finally {
      setScoringIntel(false);
    }
  };

  const handleRewrite = async (outline = null) => {
    // onClick={onRewrite} passes the click event as the first arg — only a real
    // outline array is a payload; anything else means dimension-driven rewrite.
    outline = Array.isArray(outline) ? outline : null;
    setRewriting(true);
    setRewriteError(null);
    setApplied(false);
    setApplyError(null);
    try {
      const result = await api.rewriteArticle(parseInt(id), outline ? { outline } : {});
      setRewriteResult(result);
    } catch (e) {
      setRewriteError(e.response?.data?.detail || "Article rewrite failed");
    } finally {
      setRewriting(false);
    }
  };

  const handleApplyRewrite = async () => {
    setApplying(true);
    setApplyError(null);
    try {
      await api.applyArticleRewrite(parseInt(id), {
        revised_content: rewriteResult.revised_content,
        targeted_dimensions: rewriteResult.targeted_dimensions,
        before: rewriteResult.before,
        after: rewriteResult.after,
      });
      const updated = await api.getArticle(parseInt(id));
      setArticle(updated);
      loadEditorContent(await api.getArticleRawContent(parseInt(id)));
      setApplied(true);
    } catch (e) {
      setApplyError(e.response?.data?.detail || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveResult(null);
    try {
      const html = await valueToHtml(editorRef.current?.children);
      await api.updateArticleContent(parseInt(id), html);
      setSaveResult({ success: true, message: "Saved to CMS" });
      setTimeout(() => setSaveResult(null), 3000);
    } catch (e) {
      setSaveResult({ success: false, message: e.response?.data?.detail || "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    const isDraft = article.status === "draft" || article.status == null;
    const newStatus = isDraft ? "publish" : "draft";
    setPublishing(true);
    setPublishResult(null);
    try {
      const result = await api.updateArticleStatus(parseInt(id), newStatus);
      setArticle(prev => ({ ...prev, status: result.status }));
      setPublishResult({ success: true, message: newStatus === "publish" ? "Published!" : "Reverted to draft" });
      setTimeout(() => setPublishResult(null), 3000);
    } catch (e) {
      setPublishResult({ success: false, message: e.response?.data?.detail || "Status update failed" });
    } finally {
      setPublishing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const updated = await api.refreshArticleFromWp(parseInt(id));
      setArticle(prev => ({ ...prev, ...updated }));
      loadEditorContent(await api.getArticleRawContent(parseInt(id)));
    } catch (e) {
      console.error("Refresh failed:", e);
    } finally {
      setRefreshing(false);
    }
  };

  const handleTranslate = async () => {
    setTranslating(true);
    setTranslateResult(null);
    setTranslateError(null);
    setPushResult(null);
    try {
      const result = await api.translateArticle(parseInt(id), translateLang);
      setTranslateResult(result);
    } catch (e) {
      setTranslateError(e.response?.data?.detail || "Translation failed");
    } finally {
      setTranslating(false);
    }
  };

  const handlePushTranslation = async () => {
    if (!translateResult?.id) return;
    setPushing(true);
    try {
      const result = await api.pushTranslation(translateResult.id);
      setPushResult({ success: true, edit_link: result.edit_link });
      setTranslateResult(prev => ({ ...prev, status: "pushed" }));
    } catch (e) {
      setPushResult({ success: false, message: e.response?.data?.detail || "Push failed" });
    } finally {
      setPushing(false);
    }
  };

  const handleCopyAll = async () => {
    try {
      await navigator.clipboard.writeText(valueToPlainText(editorRef.current?.children ?? []));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const startEditMeta = (field) => {
    cancelMetaRef.current = false;
    setMetaError(null);
    if (field === "title") setTitleDraft(article?.title || "");
    else setDescDraft(metaDescription || "");
    setEditingMeta(field);
  };

  const commitMeta = async (field) => {
    const cancelled = cancelMetaRef.current;
    cancelMetaRef.current = false;
    setEditingMeta(null);
    if (cancelled) return;

    const isTitle = field === "title";
    const value = (isTitle ? titleDraft : descDraft).trim();
    const current = (isTitle ? article?.title || "" : metaDescription || "").trim();
    if (value === current) return;
    if (isTitle && !value) {
      setMetaError("Title cannot be empty");
      return;
    }

    setSavingMeta(true);
    setMetaError(null);
    try {
      const result = await api.updateArticleMeta(parseInt(id), {
        [isTitle ? "title" : "description"]: value,
      });
      if (isTitle) setArticle(prev => ({ ...prev, title: result.title ?? value }));
      else setMetaDescription(result.description ?? value);
      setSaveResult({ success: true, message: isTitle ? "Title saved" : "Meta description saved" });
      setTimeout(() => setSaveResult(null), 3000);
    } catch (e) {
      setMetaError(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingMeta(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <p className="text-sm text-zinc-400">Loading article…</p>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="h-screen flex items-center justify-center">
        <p className="text-sm text-zinc-400">Article not found.</p>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-zinc-100 px-4 py-2.5 flex items-center gap-3 z-10">
        <button
          onClick={() => navigate("/dashboard/articles")}
          className="flex items-center gap-1.5 text-xs font-bold text-zinc-400 hover:text-zinc-900 transition-colors flex-shrink-0"
        >
          <ArrowLeft size={14} strokeWidth={2} />
          Articles
        </button>

        <div className="w-px h-4 bg-zinc-200 flex-shrink-0" />

        <div className="flex-1 min-w-0">
          {editingMeta === "title" ? (
            <input
              autoFocus
              value={titleDraft}
              disabled={savingMeta}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={() => commitMeta("title")}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
                else if (e.key === "Escape") { cancelMetaRef.current = true; e.currentTarget.blur(); }
              }}
              title="Edit title"
              className="w-full text-sm font-bold text-zinc-900 bg-transparent border-b border-zinc-300 focus:outline-none focus:border-zinc-900 disabled:opacity-60"
            />
          ) : (
            <button
              type="button"
              onClick={() => startEditMeta("title")}
              disabled={savingMeta}
              title="Edit title"
              className="group flex items-center gap-1.5 min-w-0 max-w-full text-left disabled:opacity-60"
            >
              <h1 className="text-sm font-bold text-zinc-900 truncate">{article.title}</h1>
              <Pencil size={11} strokeWidth={2} className="flex-shrink-0 text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          )}
          <div className="flex items-center gap-2 min-w-0">
            {article.primary_topic && (
              <span className="flex-shrink-0 text-[10px] text-zinc-400">{article.primary_topic}</span>
            )}
            {editingMeta === "description" ? (
              <input
                autoFocus
                value={descDraft}
                disabled={savingMeta}
                placeholder="Meta description"
                onChange={(e) => setDescDraft(e.target.value)}
                onBlur={() => commitMeta("description")}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
                  else if (e.key === "Escape") { cancelMetaRef.current = true; e.currentTarget.blur(); }
                }}
                title="Edit meta description"
                className="flex-1 min-w-0 text-[10px] text-zinc-700 bg-transparent border-b border-zinc-300 focus:outline-none focus:border-zinc-900 disabled:opacity-60"
              />
            ) : (
              <button
                type="button"
                onClick={() => startEditMeta("description")}
                disabled={savingMeta}
                title="Edit meta description"
                className="group flex items-center gap-1 min-w-0 text-left disabled:opacity-60"
              >
                <span className={`text-[10px] truncate ${metaDescription ? "text-zinc-400" : "text-zinc-300 italic"}`}>
                  {metaDescription || "Add meta description"}
                </span>
                <Pencil size={10} strokeWidth={2} className="flex-shrink-0 text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            )}
            {metaError && (
              <span className="flex-shrink-0 text-[10px] text-red-500 truncate max-w-[200px]" title={metaError}>{metaError}</span>
            )}
          </div>
        </div>

        {wordCount > 0 && (() => {
          const inRange = wordCount >= 1500 && wordCount <= 2500;
          const low = wordCount < 1500;
          const color = inRange ? "#008A33" : low ? "#FF8C00" : "#6B7280";
          const pct = Math.min(wordCount / 2500, 1);
          return (
            <div className="flex-shrink-0 flex flex-col items-end gap-0.5" title="Target: 1,500 – 2,500 words">
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] font-bold tabular-nums" style={{ color }}>{wordCount.toLocaleString()}</span>
                <span className="text-[10px] text-zinc-400">/ 2,500 words</span>
              </div>
              <div className="w-20 h-1 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${pct * 100}%`, backgroundColor: color }} />
              </div>
            </div>
          );
        })()}

        {(saveResult || publishResult) && (
          <span
            className={`text-[11px] font-bold flex-shrink-0 max-w-[220px] truncate ${(saveResult || publishResult).success ? "text-green-600" : "text-red-500"}`}
            title={(saveResult || publishResult).message}
          >
            {(saveResult || publishResult).message}
          </span>
        )}

        {hasCustomComponents && (
          <span
            className="text-[11px] font-bold flex-shrink-0 max-w-[240px] truncate text-amber-600"
            title="This content contains custom components that can't be edited safely here yet — edit it directly in your CMS instead."
          >
            Custom components — edit in your CMS
          </span>
        )}

        {article.wp_url && (
          <a
            href={article.wp_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 opacity-50 hover:opacity-100 transition-opacity"
            title="Open in live site"
          >
            <ArrowUpRight size={15} className="text-zinc-900" />
          </a>
        )}

        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex-shrink-0 p-1.5 rounded-lg hover:bg-zinc-100 transition-colors text-zinc-400 hover:text-zinc-700 disabled:opacity-40"
          title="Refresh from CMS"
        >
          <RefreshCw size={15} strokeWidth={2} className={refreshing ? "animate-spin" : ""} />
        </button>

        <button
          onClick={handlePublish}
          disabled={publishing}
          className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold border transition-colors rounded-lg disabled:opacity-50 ${
            article.status === "publish"
              ? "border-zinc-200 text-zinc-600 hover:bg-zinc-50"
              : "border-green-600 text-green-700 bg-green-50 hover:bg-green-100"
          }`}
          title={article.status === "publish" ? "Revert to draft" : "Publish to CMS"}
        >
          {publishing ? "…" : article.status === "publish" ? "Revert to Draft" : "Publish"}
        </button>

        <button
          onClick={() => { setShowTranslateModal(true); setTranslateResult(null); setTranslateError(null); setPushResult(null); }}
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold border border-zinc-200 text-zinc-600 hover:bg-zinc-50 transition-colors rounded-lg"
          title="Translate article"
        >
          <Languages size={12} strokeWidth={2} />
          Translate
        </button>

        <button
          onClick={handleCopyAll}
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold border border-zinc-200 text-zinc-600 hover:bg-zinc-50 transition-colors rounded-lg"
          title="Copy all text"
        >
          {copied ? <Check size={12} strokeWidth={2.5} className="text-green-600" /> : <Copy size={12} strokeWidth={2} />}
          {copied ? "Copied!" : "Copy All"}
        </button>

        <button
          onClick={handleSave}
          disabled={saving || hasCustomComponents}
          title={hasCustomComponents ? "This content contains custom components that can't be edited safely here yet — edit it directly in your CMS instead." : undefined}
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold text-white rounded-lg disabled:opacity-50 transition-colors"
          style={{ backgroundColor: PRIMARY }}
        >
          <Save size={12} strokeWidth={2} />
          {saving ? "Saving…" : "Save to CMS"}
        </button>

        <button
          onClick={() => setSidebarOpen(o => !o)}
          className="flex-shrink-0 p-1.5 rounded-lg hover:bg-zinc-100 transition-colors text-zinc-400 hover:text-zinc-900"
          title={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
        >
          {sidebarOpen ? <PanelRightClose size={16} strokeWidth={2} /> : <PanelRight size={16} strokeWidth={2} />}
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 flex overflow-hidden">
        {/* Editor — Plate handles its own scroll inside EditorContainer so the
            fixed toolbar stays pinned while the writing column scrolls. */}
        <div className="flex-1 overflow-hidden bg-white">
          {!contentReady ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm text-zinc-400">Loading content…</p>
            </div>
          ) : (
            <PlateArticleEditor
              key={editorKey}
              initialValue={editorValue}
              onWordCount={setWordCount}
              onEditorReady={(ed) => { editorRef.current = ed; }}
              className="h-full"
            />
          )}
        </div>

        {/* Sidebar */}
        {sidebarOpen && (
          <div className="w-80 flex-shrink-0 border-l border-zinc-100 overflow-y-auto bg-zinc-50 p-3 space-y-3">
            {article?.wp_post_id != null && (
              <OutlinePanel articleId={article.wp_post_id} rewriting={rewriting} onRewriteFromOutline={handleRewrite} />
            )}
            <OpportunityPanel article={article} />
            <RankingAttributionPanel article={article} />
            <ScoreSummaryPanel article={article} />
            <AEODetailPanel article={article} />
            <PersonaFitPanel article={article} onScore={handleScorePersona} scoring={scoringPersona} scoreError={personaScoreError} />
            <ArticleIntelligencePanel
              article={article}
              onScore={handleScoreIntelligence}
              scoring={scoringIntel}
              scoreError={intelScoreError}
              onRewrite={handleRewrite}
              rewriting={rewriting}
              rewriteError={rewriteError}
              rewriteResult={rewriteResult}
              onApply={handleApplyRewrite}
              applying={applying}
              applyError={applyError}
              applied={applied}
            />
            <LinksPanel article={article} />
            <GSCPanel article={article} />
            <IndexingPanel article={article} gscPropertyUrl={gscPropertyUrl} />
            <EntityPanel article={article} onExtract={handleExtractEntities} extracting={extracting} />
            <SchemaMarkupPanel article={article} />
            <TopicsPanel article={article} />
            <RelatedPanel article={article} />
          </div>
        )}
      </div>

      {/* Translate modal */}
      {showTranslateModal && (
        <div
          className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center"
          onClick={() => setShowTranslateModal(false)}
        >
          <div
            className="bg-white rounded-xl border border-zinc-200 shadow-xl w-[480px] max-w-[90vw] p-6 space-y-4"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Languages size={16} className="text-zinc-700" />
                <h2 className="text-sm font-bold text-zinc-900">Translate Article</h2>
              </div>
              <button
                onClick={() => setShowTranslateModal(false)}
                className="text-zinc-400 hover:text-zinc-900 transition-colors"
              >
                <X size={16} strokeWidth={2} />
              </button>
            </div>

            <div>
              <label className="block text-[9px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-1">
                Target Language
              </label>
              <select
                value={translateLang}
                onChange={e => { setTranslateLang(e.target.value); setTranslateResult(null); setTranslateError(null); setPushResult(null); }}
                className="w-full px-3 py-2.5 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 bg-white"
                style={{ "--tw-ring-color": PRIMARY }}
              >
                <option value="">Select language…</option>
                {TRANSLATE_LANGUAGES.map(l => (
                  <option key={l.code} value={l.code}>{l.name}</option>
                ))}
              </select>
            </div>

            <button
              onClick={handleTranslate}
              disabled={!translateLang || translating}
              className="w-full px-4 py-2.5 text-xs font-bold text-white rounded-lg disabled:opacity-40 transition-colors"
              style={{ backgroundColor: PRIMARY }}
            >
              {translating ? "Translating…" : "Translate"}
            </button>

            {translateError && (
              <p className="text-xs text-red-500">{translateError}</p>
            )}

            {translateResult && (
              <div className="space-y-3">
                <div className="p-3 bg-zinc-50 rounded-lg border border-zinc-100">
                  <p className="text-[9px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-1">Translated Title</p>
                  <p className="text-sm text-zinc-900">{translateResult.translated_title}</p>
                </div>

                <button
                  onClick={handlePushTranslation}
                  disabled={pushing || translateResult.status === "pushed"}
                  className="w-full px-4 py-2.5 text-xs font-bold border border-green-600 text-green-700 bg-green-50 hover:bg-green-100 rounded-lg disabled:opacity-40 transition-colors"
                >
                  {pushing ? "Pushing…" : translateResult.status === "pushed" ? "Pushed to CMS" : "Push to CMS as Draft"}
                </button>

                {pushResult && (
                  pushResult.success ? (pushResult.edit_link ? (
                    <a
                      href={pushResult.edit_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-xs text-green-700 hover:underline"
                    >
                      <ExternalLink size={12} /> Open draft in CMS
                    </a>
                  ) : (
                    <p className="text-xs text-green-700">Draft created in CMS.</p>
                  )) : (
                    <p className="text-xs text-red-500">{pushResult.message}</p>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
